import { SellTicketModelType } from '@teinor/erp/types/company/order/sell/sellTicket'
import AppStyleDecider from '../../../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableTopHeader from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import { FormElement, FormRender } from '../../../../../../../../../../../forms/FormRenderer/types'
import { useOrderITHeaderHandleChangeLogic } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/useOrderITHeaderHandleChange'
import { displayDate } from '../../../../../../../../../../../utils/globals/date'
import useSellTicketHeaderUrls from '../../../../../../../../../../../utils/hooks/sells/orders/useSellTicketHeaderUrls'
import { ComponentTabObject } from '../../../../../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import SellReadOnlyHeaderFinancialRows from '../../../../../headers/SellReadOnlyHeaderFinancialRows'
import { useSellReadOnlyHeaderMainLogic } from '../../../../../headers/SellReadOnlyHeaderMain'
import SellTicketReadOnlyHeaderMain from '../../../../header/SellTicketReadOnlyHeaderMain'

type SellTicketFinishedStepITHeaderProps = InputTableLeftColumnCompleteRequiredProps & {}

const SellTicketFinishedStepITHeader: React.FC<SellTicketFinishedStepITHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={SellTicketFinishedStepITHeaderDefault} NewComponent={SellTicketFinishedStepITHeaderNew} {...rest} />
}

const SellTicketFinishedStepITHeaderDefault: React.FC<SellTicketFinishedStepITHeaderProps> = ({ ...rest }) => {
    const { t } = useTwinTranslation()
    const orderHeaderURLs = useSellTicketHeaderUrls()
    const tabs: ComponentTabObject = {
        'main': {
            component: SellTicketReadOnlyHeaderMain,
            text: t('dataTicket', 'Datos del ticket'),
            extraProps: {  }
        },
        'financialRow': {
            component: SellReadOnlyHeaderFinancialRows,
            text: t('discountsAndRecharges', 'Descuentos y recargos'),
            permission: 'sell.tickets',
            extraProps: { orderHeaderURLs }
        },
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const SellTicketFinishedStepITHeaderNew: React.FC<SellTicketFinishedStepITHeaderProps> = ({ setCompleteData, headerData, updateDataURL, getCompleteData }) => {
    const { t } = useTwinTranslation()
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData, updateDataURL })
    const { memorizedAddresses, activeCurrencies } = useSellReadOnlyHeaderMainLogic({ headerData: headerData || {}, setCompleteData, handleChange })
    let extraFields: FormElement<keyof SellTicketModelType>[] = []
    if (headerData?.CustomerId) {
        extraFields.push({
            name: 'CustomerId',
            component: 'Custom',
            render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData || {}} />,
            hideSectionByDefault: true
        },)
    }
    if (headerData?.CustomerAddressId) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} />
            },
            extraProps: {
                customerAddress: memorizedAddresses.customerAddress
            },
            hideSectionByDefault: true
        })
    }
    const sections: FormRender<SellTicketModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('assignedN', 'Nº asignado')} value={value} className={(headerData?.full_assigned_number ? '' : 'text-red-BA')} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'CashRegisterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('cashRegister', 'Caja')} value={headerData?.CashRegister?.full_assigned_number} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('date', 'Fecha')} value={displayDate(new Date(value))} />
                },
                {
                    name: 'CreateEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('employee', 'Empleado')} value={headerData?.RepresentativeEmployee?.fullname_short} />
                },
                ...extraFields,
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={memorizedAddresses.workingCenterAddress} />
                    },
                    extraProps: {
                        workingCenterAddress: memorizedAddresses.workingCenterAddress
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={headerData?.WorkingCenter.name || ''} />,
                },
                {
                    name: 'WorkingCenterStationId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('workingCenterStation', 'Puesto')} value={headerData?.WorkingCenterStation?.name || ''} />,
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('billingBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} />,
                    hideSectionByDefault: true
                },
            ]
        }
    ]
    return (
        <InputTableTopHeader handleChange={handleChange} sections={sections} headerData={headerData} getCompleteData={getCompleteData} />
    )
}

export default SellTicketFinishedStepITHeader
